import React, { useMemo, useState } from 'react';
import { Badge, Button, Col, Container, Row, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { useRouteMatch, Link } from 'react-router-dom';
import DataTable from '../../components/DataTable';
import Empty from '../../components/Empty';
import { IoEyeOutline } from 'react-icons/io5';
import { AiOutlineDelete } from 'react-icons/ai';
import { formatDate, isNil } from '../../utilities/common';
import { dataTableNumericSortHandler, formatNumber } from '../reports/reportHelper';
import { fetchSavedSenarios } from '../manage-users/usersSlice';
import { deleteSavedScenarioOutputApi } from './scenarioApi';
import { toast } from 'react-toastify';
import DeleteConfirmDialog from '../../components/DeleteConfirmDialog';
import ScenarioIcon from './components/ScenarioIcon';
import { getScenarioDefinitions, ICON_BG } from './scenarioConfig';

const SavedScenariosTab = () => {
  const dispatch = useDispatch();
  const match = useRouteMatch();
  const { t } = useTranslation();
  const [deletingItem, setDeletingItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const userInfo = useSelector((state) => state.users.user);
  const scenarios = useSelector((state) => state.users.savedSenarios);

  const deleteSavedScenario = async () => {
    try {
      setIsLoading(true);
      await deleteSavedScenarioOutputApi(userInfo.usrOrgId, deletingItem.id);
      dispatch(fetchSavedSenarios({ orgId: userInfo.usrOrgId }));
      setDeletingItem(null);
    } catch (er) {
      toast.error(t('Failed to delete scenario'));
    }
    finally {
      setIsLoading(false);
    }
  };

  const scenariosConfig = getScenarioDefinitions(t);

  const columns = useMemo(
    () => [
      {
        Header: t('Scenario Name'),
        accessor: 'name',
        Cell: ({ row }) => {
          const config = scenariosConfig[row.original?.model];
          return (
            <div className="d-flex align-items-center">
              <div
                className="mr-3 d-flex align-items-center justify-content-center rounded-6"
                style={{
                  width: 40,
                  height: 40,
                  backgroundColor: '#f3f4f5',
                }}
              >
                <ScenarioIcon
                  type={config.icon}
                  style={{ width: 20, height: 20, color: '#778293' }}
                />
              </div>
              <div>
                <div className="text-muted">{row.original?.name}</div>
              </div>
            </div>
          );
        },
        sortType: 'string'
      },
      {
        Header: t('Type'),
        accessor: 'model',
        Cell: ({ row }) => {
          const config = scenariosConfig[row.original?.model];
          return (
            <Badge
              style={{
                backgroundColor: ICON_BG[config.color],
              }}
              className={`text-${config.color} p-2 rounded-6`}
            >
              {config.name}
            </Badge>
          );
        }
      },
      {
        Header: t('Last Edited'),
        accessor: (row) => new Date(row.updatedAt),
        Cell: ({ row }) => formatDate(row.original?.updatedAt),
        sortType: 'datetime'
      },
      {
        Header: t('Cost Savings'),
        accessor: "costSavings",
        Cell: ({ row }) => isNil(row.original?.costSavings) ? '-' : `£${formatNumber(row.original?.costSavings, 0, true)}/yr`,
        sortType: dataTableNumericSortHandler,
        className: 'text-right'
      },
      {
        Header: t('Carbon Reduction'),
        accessor: "carbonReduction",
        Cell: ({ row }) => isNil(row.original?.carbonReduction) ? '-' : `${formatNumber(row.original?.carbonReduction)} tCO₂e/yr`,
        sortType: dataTableNumericSortHandler,
        className: 'text-right'
      },
      {
        Header: '',
        accessor: 'actions',
        actionColumn: true,
        disableGlobalFilter: true,
        disableSortBy: true,
        Cell: ({ row }) => {
          const scenario = row.original;
          return (
            <div className="d-flex justify-content-end action-buttons">
              <Link to={`${match.url}/${scenario.id}/view`}>
                <Button variant="outline-secondary" className="mr-2">
                  <IoEyeOutline />
                </Button>
              </Link>
              <Button
                variant="outline-danger"
                onClick={() => setDeletingItem(scenario)}
              >
                <AiOutlineDelete />
              </Button>
            </div>
          );
        }
      }
    ],
    [match.url, t, setDeletingItem, scenariosConfig]
  );

  return (
    <Container fluid className="pt-4 px-4">
      <Row>
        <Col>
          <h5 className="mb-2">{t('My Saved Scenarios')}</h5>
          <p className="text-muted">
            {t('Manage and review your saved scenario models and forecasts')}
          </p>
        </Col>
      </Row>

      {!scenarios && <Spinner animation="grow" />}

      {scenarios?.length > 0 && (
        <Row>
          <Col>
            <DataTable columns={columns} data={scenarios} />
          </Col>
        </Row>
      )}

      {scenarios?.length === 0 && (
        <Empty title={t('No saved scenarios')} />
      )}
      <DeleteConfirmDialog
        visible={deletingItem !== null}
        headerTitle={t('Delete Saved Scenarios')}
        onDiscardHandler={() => setDeletingItem(null)}
        onDeleteHandler={deleteSavedScenario}
        isLoading={isLoading}
      >
        <p>
          <Trans
            i18nKey="Do you really want to delete the saved scenarios <bold>{{name}}</bold>?"
            values={{ name: deletingItem?.name }}
            components={{ bold: <strong /> }}
          />
        </p>
      </DeleteConfirmDialog>
    </Container>
  );
};

export default SavedScenariosTab;
